/**
 * Goethe-Zertifikat Schreiben and Sprechen (D-118).
 * - Schreiben: the parts of the level, each with its situation, the points to cover, the
 *   register and the number of words (counted as the server counts them; under half the words,
 *   the part is worth 0: « E rule »). The A1 form and the C2 rewrites are answered field by
 *   field; the C2 part 2 offers 4 topics. Saved as the learner types.
 * - Sprechen: the preparation time of the level (B1 to C2), the choice of the topics, then each
 *   part with the live examiners: at A1 the AI also plays the other candidates of the group, from
 *   A2 to C1 the partner, at C2 the examiner alone defends the other position. Each turn is
 *   transcribed in German and answered; the part is recorded and sent.
 */

import { academyAttemptsNextCreate, academyAttemptsRecordingCreate, academyAttemptsSpeakingCreate, academyAttemptsTextCreate } from "@agencyos/api-client";
import { type GoetheSitting, type GoetheTask, type TurnListening, listenTurn, speakerOf, startTurn, wordsStatus } from "@agencyos/core";
import { Badge, Button } from "@agencyos/ui-web";
import { Hourglass, MessageSquareReply, Mic, MicOff, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { closeStream, deviceVoices, extensionFor, openMicrophone, recorderMimeType, recorderOptions, sendRecording, stopSpeaking, useMicLevel, useMicStayedSilent, utter } from "../audio";
import { MethodLink } from "../ielts/MethodLink";
import { usePhaseCountdown } from "../shared";
import { Examiner, LevelMeter } from "../SpeakingSection";
import { clock } from "../text";
import { isNoOralCredits } from "@agencyos/core";
import { OralCreditsNotice, reportNoOralCredits } from "../TrainerReview";

function str(value: unknown): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
}

function list(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((v): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v)) : [];
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map(str).filter(Boolean) : [];
}

// --- Schreiben ---------------------------------------------------------------------------------

function WritingTask({ attemptId, task, level }: { attemptId: string; task: GoetheTask; level: string }) {
  const { t } = useTranslation();
  const material = task.material;
  const kind = str(material.kind);
  const [text, setText] = useState(task.text);
  const [responses, setResponses] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(task.responses).map(([k, v]) => [k, str(v)])));
  const [saved, setSaved] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const timer = useRef<number | null>(null);
  const latest = useRef({ text, responses });
  latest.current = { text, responses };

  const save = useCallback(async () => {
    setSaved("saving");
    try {
      await academyAttemptsTextCreate(attemptId, { task: task.id, text: latest.current.text, responses: latest.current.responses });
      setSaved("saved");
    } catch {
      setSaved("error");
    }
  }, [attemptId, task.id]);
  const schedule = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save(), 1200);
  };
  useEffect(() => () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      void save();
    }
  }, [save]);
  const setField = (key: string, value: string) => {
    setResponses((r) => ({ ...r, [key]: value }));
    schedule();
  };
  const words = wordsStatus(text, task.limits);
  const form = typeof material.form === "object" && material.form !== null ? (material.form as Record<string, unknown>) : {};
  const post = typeof material.post === "object" && material.post !== null ? (material.post as Record<string, unknown>) : null;
  const example = typeof material.example === "object" && material.example !== null ? (material.example as Record<string, unknown>) : null;
  const topics = list(material.topics);

  return (
    <section className="ac-question__sheet ac-goethe-writing" aria-labelledby={`goethe-w-${task.teil}`}>
      <h2 id={`goethe-w-${task.teil}`} className="ac-title-3">
        {t("goethe.player.teil", { n: task.teil })} · <span className="ao-num">{t("goethe.player.points", { n: task.pointsMax })}</span>
      </h2>
      {str(material.situation) && (
        <p className="ac-goethe-situation" lang="de">
          {str(material.situation)}
        </p>
      )}
      {post && (
        <blockquote className="ac-goethe-post" lang="de">
          <strong>{str(post.author)}</strong>
          <p>{str(post.text)}</p>
        </blockquote>
      )}
      {kind === "umformung" && str(material.text) && (
        <p className="ac-goethe-text" lang="de">
          {str(material.text)}
        </p>
      )}
      <p className="ac-prompt" lang="de">
        {task.prompt}
      </p>
      {strings(material.points_to_cover).length > 0 && (
        <ul className="ac-goethe-points" lang="de">
          {strings(material.points_to_cover).map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      )}
      {kind === "formular" && (
        <div className="ac-goethe-form" lang="de">
          <p className="ac-goethe-form__title">{str(form.title)}</p>
          {list(form.fields).map((field, index) => {
            const n = str(field.n);
            return (
              <label key={index} className="ac-goethe-form__row">
                <span>
                  {n && <span className="ac-goethe-item__n ao-num">{n}</span>} {str(field.label)}
                </span>
                {n ? (
                  <span className="ac-goethe-form__input">
                    {str(field.value_prefix) && <span className="ao-num">{str(field.value_prefix)} </span>}
                    <input type="text" autoComplete="off" spellCheck={false} value={responses[n] ?? ""} onChange={(e) => setField(n, e.target.value)} aria-label={str(field.label)} />
                  </span>
                ) : (
                  <span className="ac-goethe-form__value">{str(field.value)}</span>
                )}
              </label>
            );
          })}
        </div>
      )}
      {kind === "umformung" && (
        <ol className="ac-goethe-rewrites" lang="de">
          {example && (
            <li className="ac-goethe-rewrite ac-goethe-rewrite--example">
              <p>
                <span className="ac-goethe-item__n">0</span> {str(example.original)} <strong>({str(example.keyword)})</strong>
              </p>
              <p>{str(example.answer)}</p>
            </li>
          )}
          {list(material.questions).map((row) => {
            const n = str(row.n);
            return (
              <li key={n} className="ac-goethe-rewrite">
                <p>
                  <span className="ac-goethe-item__n ao-num">{n}</span> {str(row.original)} <strong>({str(row.keyword)})</strong>
                </p>
                <input type="text" autoComplete="off" value={responses[n] ?? ""} onChange={(e) => setField(n, e.target.value)} aria-label={t("goethe.player.rewrite", { n })} />
              </li>
            );
          })}
        </ol>
      )}
      {topics.length > 0 && (
        <fieldset className="ac-goethe-topics" lang="de">
          <legend>{t("goethe.player.chooseTopic")}</legend>
          {topics.map((topic) => (
            <label key={str(topic.no)} className="ac-goethe-topic">
              <input type="radio" name={`topic-${task.id}`} checked={responses.topic === str(topic.no)} onChange={() => setField("topic", str(topic.no))} />
              <span>
                <strong>{str(topic.title)}</strong> <Badge>{t(`goethe.player.topicType.${str(topic.type) || "free"}`)}</Badge>
                <span className="ac-goethe-topic__prompt">{str(topic.prompt)}</span>
                {strings(topic.quotes).map((quote) => (
                  <q key={quote}>{quote}</q>
                ))}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {kind !== "formular" && kind !== "umformung" && (
        <>
          <textarea
            className="ac-goethe-textarea"
            lang="de"
            spellCheck={false}
            value={text}
            rows={10}
            aria-label={t("goethe.player.yourText")}
            onChange={(e) => {
              setText(e.target.value);
              schedule();
            }}
          />
          <p className={`ac-caption ao-num${words.halfWarning ? " ko" : ""}`} role="status">
            {t("goethe.player.words", { count: words.count })}
            {words.min ? ` · ${t("goethe.player.wordsAsked", { min: words.min, max: words.max })}` : ""}
            {words.halfWarning && words.count > 0 ? ` · ${t("goethe.player.eRule")}` : ""}
          </p>
        </>
      )}
      <p className="ac-footnote" aria-live="polite">
        {saved === "saving" ? t("goethe.player.saving") : saved === "saved" ? t("goethe.player.saved") : saved === "error" ? t("academy.player.networkError") : ""}
      </p>
      <MethodLink exam="goethe" skill="ee" task={task.teil} type={level} newTab />
    </section>
  );
}

export function GoetheWriting({ attemptId, state, onChanged }: { attemptId: string; state: GoetheSitting; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [moving, setMoving] = useState(false);
  const task = state.tasks[Math.min(tab, state.tasks.length - 1)];
  const finish = async () => {
    setMoving(true);
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setMoving(false);
    }
  };
  return (
    <div className="ac-goethe-writing-module">
      {state.tasks.length > 1 && (
        <nav className="ac-goethe-tabs" aria-label={t("goethe.player.parts")}>
          {state.tasks.map((item, index) => (
            <button key={item.id} type="button" className={`ac-goethe-tab${index === tab ? " ac-goethe-tab--on" : ""}`} aria-current={index === tab} onClick={() => setTab(index)}>
              {t("goethe.player.teil", { n: item.teil })}
            </button>
          ))}
        </nav>
      )}
      {task && <WritingTask key={task.id} attemptId={attemptId} task={task} level={state.level} />}
      <div className="ac-finish">
        {tab < state.tasks.length - 1 && (
          <Button variant="secondary" onClick={() => setTab(tab + 1)}>
            {t("goethe.player.nextPart")}
          </Button>
        )}
        <Button size="lg" disabled={moving} onClick={() => void finish()}>
          {t(state.block ? "conformity.goethe.finishBlock" : "goethe.player.finishModule")}
        </Button>
      </div>
    </div>
  );
}

// --- Sprechen ----------------------------------------------------------------------------------

const VOICE_LEVEL = 0.06;
type LiveStatus = "idle" | "listening" | "sending" | "speaking";
type Phase = "prep" | "mic" | "ready" | "opening" | "record" | "upload" | "uploadError" | "between" | "review";
interface Line {
  role: "learner" | "examiner" | "partner";
  text: string;
}

/** A German voice of the device; the partner speaks a little higher than the examiner. */
async function speakGerman(text: string, role: "examiner" | "partner"): Promise<void> {
  if (!text.trim()) return;
  const voices = (await deviceVoices()).filter((v) => v.lang.toLowerCase().startsWith("de"));
  stopSpeaking();
  await utter(text, { lang: "de-DE", voice: voices[role === "partner" ? Math.min(1, voices.length - 1) : 0], rate: 0.95, pitch: role === "partner" ? 1.12 : 1 });
}

function seconds(task: GoetheTask | undefined): number {
  const limits = task?.limits ?? {};
  const minutes = Number(limits.minutes ?? limits.minutes_per_candidate ?? 0) || Number(limits.talk_minutes ?? 0) + Number(limits.qa_minutes ?? 0) || 5;
  return Math.round(minutes * 60);
}

/** The material of a speaking part: cards, topics, diary, leading points (never the partner's). */
function SpeakingMaterial({ task, topic, onTopic, side, onSide }: { task: GoetheTask; topic: number; onTopic: (n: number) => void; side: string; onSide: (s: string) => void }) {
  const { t } = useTranslation();
  const m = task.material;
  const topics = list(m.topics);
  const card = typeof m.card === "object" && m.card !== null ? (m.card as Record<string, unknown>) : null;
  return (
    <div className="ac-goethe-material" lang="de">
      {str(m.situation) && <p className="ac-goethe-situation">{str(m.situation)}</p>}
      {str(m.task) && <p className="ac-goethe-situation">{str(m.task)}</p>}
      {str(m.question) && <p className="ac-prompt">{str(m.question)}</p>}
      {str(m.input_text) && <p className="ac-goethe-text">{str(m.input_text)}</p>}
      {strings(m.keywords).length > 0 && <p className="ac-goethe-keywords">{strings(m.keywords).join(" · ")}</p>}
      {strings(m.leitpunkte).length > 0 && (
        <ul className="ac-goethe-points">
          {strings(m.leitpunkte).map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {strings(m.bullets).length > 0 && (
        <ul className="ac-goethe-points">
          {strings(m.bullets).map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {list(m.themes).map((theme) => (
        <div key={str(theme.theme)} className="ac-goethe-cards">
          <strong>{str(theme.theme)}</strong>
          {strings(theme.cards).map((word) => (
            <span key={word} className="ac-goethe-card">
              {word}
            </span>
          ))}
        </div>
      ))}
      {Array.isArray(m.cards) && (
        <div className="ac-goethe-cards">
          {(m.cards as unknown[]).map((c, index) => (
            <span key={index} className="ac-goethe-card">
              {typeof c === "string" ? c : `${str((c as Record<string, unknown>).topic)} · ${str((c as Record<string, unknown>).keyword)}`}
            </span>
          ))}
        </div>
      )}
      {card && (
        <div className="ac-cue-card">
          <p className="ac-cue-card__topic">{str(card.question)}</p>
          <ul>
            {strings(card.prompts).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(m.calendar) && (
        <table className="ac-goethe-diary">
          <caption>{t("goethe.speaking.diary")}</caption>
          <tbody>
            {list(m.calendar).map((row, index) => (
              <tr key={index}>
                <th scope="row" className="ao-num">
                  {str(row.time)}
                </th>
                <td>{str(row.entry) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {topics.length > 0 && (
        <fieldset className="ac-goethe-topics">
          <legend>{t("goethe.player.chooseTopic")}</legend>
          {topics.map((item, index) => (
            <label key={index} className="ac-goethe-topic">
              <input type="radio" name={`speak-topic-${task.id}`} checked={topic === index} onChange={() => onTopic(index)} />
              <span>
                <strong>{str(item.title)}</strong>
                {str(item.situation) && <span className="ac-goethe-topic__prompt">{str(item.situation)}</span>}
                {strings(item.slides).length > 0 && <span className="ac-goethe-topic__prompt">{strings(item.slides).join(" · ")}</span>}
                {strings(item.bullets).length > 0 && <span className="ac-goethe-topic__prompt">{strings(item.bullets).join(" · ")}</span>}
                {str(item.input_text) && <span className="ac-goethe-topic__prompt">{str(item.input_text)}</span>}
                {strings(item.quotes).map((quote) => (
                  <q key={quote}>{quote}</q>
                ))}
              </span>
            </label>
          ))}
          {topics.some((item) => str(item.pro)) && (
            <div className="ac-goethe-sides" role="radiogroup" aria-label={t("goethe.speaking.side")}>
              {(["pro", "contra"] as const).map((key) => (
                <label key={key} className="ac-goethe-topic">
                  <input type="radio" name={`speak-side-${task.id}`} checked={side === key} onChange={() => onSide(key)} />
                  <span>
                    <strong>{t(`goethe.speaking.${key}`)}</strong> {str(topics[topic]?.[key])}
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}

function useLive({ attemptId, task, stream, level, active, onEnd }: { attemptId: string; task: GoetheTask | undefined; stream: MediaStream | null; level: number; active: boolean; onEnd: () => void }) {
  const [status, setStatus] = useState<LiveStatus>("idle");
  const [lines, setLines] = useState<Line[]>([]);
  const [offline, setOffline] = useState(false);
  const segment = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const listening = useRef<TurnListening>(startTurn(0));
  const segmentStart = useRef(0);
  const partStart = useRef(0);
  const activeRef = useRef(active);
  const statusRef = useRef<LiveStatus>("idle");
  const endRef = useRef(onEnd);
  useEffect(() => {
    endRef.current = onEnd;
  }, [onEnd]);
  const change = (next: LiveStatus) => {
    statusRef.current = next;
    setStatus(next);
  };
  const startSegment = useCallback(() => {
    if (!stream || !activeRef.current || typeof MediaRecorder === "undefined") return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    segment.current = media;
    segmentStart.current = Date.now();
    listening.current = startTurn(segmentStart.current);
    media.start();
    change("listening");
  }, [stream]);
  const answer = useCallback(
    async (blob: Blob) => {
      if (!task) return;
      change("sending");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `goethe-turn.${extensionFor(type)}`, { type });
      const offset = Math.max((segmentStart.current - partStart.current) / 1000, 0);
      let replies: { role: string; text: string }[] = [];
      let end = false;
      try {
        const data = (await academyAttemptsSpeakingCreate(attemptId, { task: task.id, audio: file, offset })).data as unknown as { transcript: string; replies?: { role: string; text: string }[]; reply: string; end: boolean };
        replies = data.replies?.length ? data.replies : data.reply ? [{ role: "examiner", text: data.reply }] : [];
        end = data.end;
        setOffline(false);
        setLines((current) => [
          ...current,
          ...(data.transcript ? [{ role: "learner" as const, text: data.transcript }] : []),
          ...replies.map((r) => ({ role: speakerOf(r.role), text: r.text })),
        ]);
      } catch (error) {
        if (isNoOralCredits(error)) reportNoOralCredits(); // D-124: polite notice, no credit left
        setOffline(true);
      }
      if (!activeRef.current) return;
      change("speaking");
      for (const reply of replies) await speakGerman(reply.text, speakerOf(reply.role));
      if (!activeRef.current) return;
      if (end) {
        change("idle");
        endRef.current();
      } else startSegment();
    },
    [attemptId, task, startSegment],
  );
  const finishTurn = useCallback(() => {
    const media = segment.current;
    if (!media || media.state !== "recording" || statusRef.current !== "listening") return;
    media.onstop = () => void answer(new Blob(chunks.current, { type: media.mimeType || "audio/webm" }));
    media.stop();
  }, [answer]);
  useEffect(() => {
    activeRef.current = active;
    if (active) {
      partStart.current = Date.now();
      startSegment();
      return;
    }
    const media = segment.current;
    if (media && media.state === "recording") {
      media.onstop = null;
      media.stop();
    }
    segment.current = null;
    change("idle");
  }, [active, startSegment]);
  useEffect(
    () => () => {
      activeRef.current = false;
      const media = segment.current;
      if (media && media.state === "recording") {
        media.onstop = null;
        media.stop();
      }
    },
    [],
  );
  useEffect(() => {
    if (!active || status !== "listening") return;
    const next = listenTurn(listening.current, level > VOICE_LEVEL, Date.now());
    listening.current = next;
    if (next.end) finishTurn();
  }, [level, active, status, finishTurn]);
  return { status, lines, setLines, offline, finishTurn };
}

export function GoetheSpeaking({ attemptId, state, onChanged }: { attemptId: string; state: GoetheSitting; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [micError, setMicError] = useState(false);
  const [phase, setPhase] = useState<Phase>(state.prep.seconds > 0 ? "prep" : "mic");
  const [done, setDone] = useState<string[]>(() => state.tasks.filter((task) => task.status && task.status !== "draft").map((task) => task.id));
  const [choices, setChoices] = useState<Record<string, { topic: number; side: string }>>({});
  const [finishing, setFinishing] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const pending = useRef<Blob | null>(null);
  const level = useMicLevel(stream);
  const micSilent = useMicStayedSilent(level, phase === "record");
  const task = state.tasks.find((item) => !done.includes(item.id));
  const choice = (id: string) => choices[id] ?? { topic: 0, side: "pro" };
  const setChoice = (id: string, value: Partial<{ topic: number; side: string }>) => setChoices((c) => ({ ...c, [id]: { ...choice(id), ...value } }));

  useEffect(
    () => () => {
      stopSpeaking();
      if (recorder.current?.state === "recording") recorder.current.stop();
    },
    [],
  );
  useEffect(() => () => closeStream(stream), [stream]);
  useEffect(() => {
    if (phase === "review") setStream(null);
  }, [phase]);

  const prepLeft = usePhaseCountdown(state.prep.seconds, phase === "prep", () => setPhase("mic"));

  const testMicrophone = async () => {
    setMicError(false);
    try {
      setStream(await openMicrophone());
      setPhase("ready");
    } catch {
      setMicError(true);
    }
  };

  const upload = useCallback(
    async (blob: Blob, current: GoetheTask) => {
      setPhase("upload");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `goethe-sprechen-${current.teil}.${extensionFor(type)}`, { type });
      try {
        await sendRecording(() => academyAttemptsRecordingCreate(attemptId, { task: current.id, audio: file }));
        pending.current = null;
        setDone((items) => [...items, current.id]);
        setPhase("between");
      } catch {
        pending.current = blob;
        setPhase("uploadError");
      }
    },
    [attemptId],
  );

  const startRecording = useCallback(() => {
    if (!stream || !task) return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, recorderOptions(mimeType));
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    const current = task;
    media.onstop = () => void upload(new Blob(chunks.current, { type: media.mimeType || mimeType || "audio/webm" }), current);
    recorder.current = media;
    media.start(1000);
    setPhase("record");
  }, [stream, task, upload]);
  const stopRecording = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);
  const live = useLive({ attemptId, task, stream, level, active: phase === "record", onEnd: stopRecording });
  const { setLines } = live;

  const beginPart = useCallback(async () => {
    if (!task) return;
    setPhase("opening");
    let replies: { role: string; text: string }[] = [];
    try {
      const picked = choice(task.id);
      const hasTopics = Array.isArray(task.material.topics);
      const data = (await academyAttemptsSpeakingCreate(attemptId, { task: task.id, ...(hasTopics ? { topic: picked.topic, side: picked.side as "pro" | "contra" } : {}) })).data as unknown as { replies?: { role: string; text: string }[]; reply: string };
      replies = data.replies?.length ? data.replies : data.reply ? [{ role: "examiner", text: data.reply }] : [];
    } catch (error) {
      if (isNoOralCredits(error)) reportNoOralCredits(); // D-124: polite notice, no credit left
      replies = [];
    }
    setLines(replies.map((r) => ({ role: speakerOf(r.role), text: r.text })));
    for (const reply of replies) await speakGerman(reply.text, speakerOf(reply.role));
    startRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, task, startRecording, setLines, choices]);

  useEffect(() => {
    if (phase !== "between") return;
    const timer = window.setTimeout(() => {
      if (task) void beginPart();
      else setPhase("review");
    }, 2200);
    return () => window.clearTimeout(timer);
  }, [phase, task, beginPart]);

  const total = seconds(task);
  const left = usePhaseCountdown(total, phase === "record", stopRecording);
  const finish = async () => {
    setFinishing(true);
    try {
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setFinishing(false);
    }
  };
  const noRecorder = typeof MediaRecorder === "undefined";

  return (
    <section className="ac-speaking ac-goethe-speaking" aria-label="Sprechen">
      <div className="ac-question__sheet ac-speaking__sheet">
        <OralCreditsNotice />
        {phase === "prep" && (
          <div className="ac-stage ac-stage--task">
            <p className="ac-player__eyebrow">
              <Hourglass aria-hidden size={14} /> {t("goethe.speaking.prep", { minutes: state.prep.minutes })}
            </p>
            <p className="ac-countdown__digits ao-num" role="timer">
              {clock(prepLeft)}
            </p>
            <p className="ac-secondary">{t(state.prep.notesAllowed ? "goethe.speaking.prepNotes" : "goethe.speaking.prepText")}</p>
            {state.tasks.map((item) => (
              <article key={item.id} className="ac-goethe-prep-part">
                <h2 className="ac-title-3">{t("goethe.player.teil", { n: item.teil })}</h2>
                <p className="ac-prompt" lang="de">
                  {item.prompt}
                </p>
                <SpeakingMaterial task={item} topic={choice(item.id).topic} onTopic={(n) => setChoice(item.id, { topic: n })} side={choice(item.id).side} onSide={(s) => setChoice(item.id, { side: s })} />
              </article>
            ))}
            <Button size="lg" onClick={() => setPhase("mic")}>
              {t("goethe.speaking.prepDone")}
            </Button>
          </div>
        )}
        {phase === "mic" && (
          <div className="ac-stage">
            <span className="ac-stage__mic" aria-hidden="true">
              <Mic size={34} strokeWidth={1.8} />
            </span>
            <h2 className="ac-title-2">{t("academy.speaking.micTitle")}</h2>
            <p className="ac-secondary ac-stage__text">{t(`goethe.setting.${state.setting || "pair"}Hint`)}</p>
            {noRecorder ? (
              <p className="ko">{t("academy.speaking.noRecorder")}</p>
            ) : (
              <Button size="lg" icon={<Mic aria-hidden size={18} />} onClick={() => void testMicrophone()}>
                {t("academy.speaking.micTest")}
              </Button>
            )}
            {micError && (
              <p className="ko ac-status" role="alert">
                <MicOff aria-hidden size={18} />
                {t("academy.speaking.micDenied")}
              </p>
            )}
          </div>
        )}
        {phase === "ready" && (
          <div className="ac-stage">
            <h2 className="ac-title-2">{t("goethe.speaking.readyTitle")}</h2>
            <LevelMeter level={level} label={t("academy.speaking.level")} />
            <ul className="ac-eo-tasks">
              {state.tasks.map((item) => (
                <li key={item.id}>
                  <span className="ac-task-tab__n ao-num" aria-hidden="true">
                    {item.teil}
                  </span>
                  <span>{str(item.limits.label && (item.limits.label as Record<string, unknown>).de) || t("goethe.player.teil", { n: item.teil })}</span>
                  <span className="ac-caption ao-num">{clock(seconds(item))}</span>
                </li>
              ))}
            </ul>
            <p className="ac-footnote ac-stage__text">{t("goethe.speaking.rules")}</p>
            <Button size="lg" onClick={() => (task ? void beginPart() : setPhase("review"))}>
              {task ? t("academy.speaking.start") : t("academy.player.continue")}
            </Button>
          </div>
        )}
        {task && (phase === "opening" || phase === "record") && (
          <div className="ac-stage ac-stage--task">
            <p className="ac-player__eyebrow">{t("goethe.player.teil", { n: task.teil })}</p>
            <p className="ac-prompt" lang="de">
              {task.prompt}
            </p>
            <SpeakingMaterial task={task} topic={choice(task.id).topic} onTopic={() => undefined} side={choice(task.id).side} onSide={() => undefined} />
            {phase === "opening" && (
              <div role="status" className="ac-stage__status">
                <Examiner speaking label={t("goethe.speaking.examinerSpeaks")} />
              </div>
            )}
            {micSilent && <p className="ac-note ac-note--warning" role="alert">{t("academy.redo.micSilent")}</p>}
            {phase === "record" && (
              <>
                <p className="ac-countdown__digits ao-num" role="timer" aria-label={t("academy.speaking.timeLeft")}>
                  <span className="ac-rec-dot" aria-hidden="true" /> {clock(left)}
                </p>
                <LevelMeter level={level} label={t("academy.speaking.level")} />
                <div className="ac-live-examiner" aria-live="polite">
                  <p className="ac-status ac-secondary" role="status">
                    {t(`goethe.speaking.status.${live.status}`)}
                  </p>
                  {live.offline && <p className="ac-footnote">{t("academyExaminer.offline")}</p>}
                  <Button icon={<MessageSquareReply aria-hidden size={16} />} onClick={live.finishTurn} disabled={live.status !== "listening"}>
                    {t("goethe.speaking.finishedTurn")}
                  </Button>
                </div>
                <Button variant="ghost" icon={<Square aria-hidden size={14} />} onClick={stopRecording}>
                  {t("goethe.speaking.endPart")}
                </Button>
              </>
            )}
            {live.lines.length > 0 && (
              <ul className="ac-transcript" aria-label={t("academyExaminer.conversation")}>
                {live.lines.map((line, index) => (
                  <li key={`${index}-${line.role}`} className={`ac-bubble ac-bubble--${line.role === "learner" ? "me" : "them"}`}>
                    <strong className="ac-bubble__who">{t(`goethe.speaking.who.${line.role}`)}</strong> <span lang="de">{line.text}</span>
                  </li>
                ))}
              </ul>
            )}
            <MethodLink exam="goethe" skill="eo" task={task.teil} type={state.level} newTab />
          </div>
        )}
        {phase === "upload" && (
          <div className="ac-stage">
            <p className="ac-status" role="status">
              {t("academy.speaking.uploading")}
            </p>
          </div>
        )}
        {phase === "uploadError" && task && (
          <div className="ac-stage" role="alert">
            <p className="ko">{t("academy.speaking.uploadError")}</p>
            <Button onClick={() => pending.current && void upload(pending.current, task)}>{t("common.retry")}</Button>
          </div>
        )}
        {phase === "between" && (
          <div className="ac-stage">
            <p className="ac-status ac-status--success" role="status">
              {t("goethe.speaking.between")}
            </p>
          </div>
        )}
        {phase === "review" && (
          <div className="ac-stage ac-stage--review">
            <h2 className="ac-title-2">{t("goethe.speaking.doneTitle")}</h2>
            <p className="ac-secondary ac-stage__text">{t("goethe.speaking.doneText")}</p>
            <Button size="lg" onClick={() => void finish()} disabled={finishing}>
              {finishing ? t("common.loading") : t("academy.speaking.finish")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
